import { useEffect, useRef, useState } from 'react';
import { useNavigation } from 'react-router';
import { useSpinDelay } from 'spin-delay';

import { cn } from '@april9au/react-common/utils';

import { useStrings } from '@/i18n/use-strings';

export function ProgressBar() {
  const { content } = useStrings();
  const transition = useNavigation();
  const busy = transition.state !== 'idle';
  const delayedPending = useSpinDelay(busy, {
    delay: 600,
    minDuration: 400,
  });
  const ref = useRef<HTMLDivElement>(null);
  const [animationComplete, setAnimationComplete] = useState(true);

  useEffect(() => {
    if (!ref.current) return;
    if (delayedPending) setAnimationComplete(false);

    const animationPromises = ref.current.getAnimations().map(({ finished }) => finished);

    void Promise.allSettled(animationPromises).then(() => {
      if (!delayedPending) setAnimationComplete(true);
    });
  }, [delayedPending]);

  return (
    <div
      role="progressbar"
      aria-hidden={delayedPending ? undefined : true}
      aria-valuetext={delayedPending ? content.content_loading : undefined}
      // `animate-pulse` only while a navigation is actually in flight: left on
      // unconditionally it repaints this strip for the life of the session,
      // which is a measurable cost on a 120 Hz screen for a bar nobody can see.
      className={cn('fixed inset-x-0 top-0 z-[9999] h-[0.20rem]', delayedPending && 'animate-pulse')}
    >
      <div
        ref={ref}
        className={cn(
          'bg-foreground h-full w-0 duration-500 ease-in-out',
          transition.state === 'idle' && (animationComplete ? 'transition-none' : 'w-full opacity-0 transition-all'),
          delayedPending && transition.state === 'submitting' && 'w-5/12',
          delayedPending && transition.state === 'loading' && 'w-8/12',
        )}
      />
    </div>
  );
}
